import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { apiService } from 'core/services/apiService';
import { getByodAppSession, resolveWorkplaceId } from 'core/utils/byodAppSession';

/**
 * OAuth initiate response interface
 */
interface OAuthInitiateResponse {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	authUrl: string;
}

export type OneDriveSourceType = 'personal_drive' | 'sharepoint_site';

export interface OneDriveRootItem {
	id: string;
	name: string;
	type: string;
	childCount?: number | null;
	size?: number | null;
	webUrl?: string | null;
	mimeType?: string | null;
	modifiedAt?: string | null;
	modifiedBy?: string | null;
}

export interface OneDriveDrive {
	id: string;
	name: string;
	driveType?: string;
	source: OneDriveSourceType | string;
	siteId?: string | null;
	siteName?: string | null;
	webUrl?: string | null;
	owner?: string | null;
	createdAt?: string | null;
	rootItems?: OneDriveRootItem[];
	rootItemsError?: string | null;
}

/**
 * Location selection persisted into partner_list.parameters
 */
export interface OneDriveLocationSelection {
	source: OneDriveSourceType;
	drive_id: string;
	drive_name: string;
	site_id?: string | null;
	site_name?: string | null;
	folder_id: string | null;
	folder_name: string | null;
}

/**
 * Partner Attempt Parameters interface for OneDrive
 */
interface PartnerAttemptParameters {
	access_token?: string;
	encrypted_refresh_token?: string;
	token_expires_in?: number;
	token_expires_at?: string;
	scope?: string;
	token_type?: string;
	id_token?: string | null;
	drives?: OneDriveDrive[];
	drive_ids?: string[];
	drives_fetched_at?: string;
	drives_fetch_error?: string | null;
	tenant?: string;
	connected_by_token?: string;
	provider?: string;
}

/**
 * Partner Attempt interface
 */
interface PartnerAttempt {
	'@id': string;
	'@type': string;
	id: number;
	parameter: PartnerAttemptParameters;
	user: string;
	workplace: string;
	type: string;
	driveId: string | null;
	createdAt: string;
}

/**
 * Partner Attempts API response (Hydra Collection or plain array)
 */
type PartnerAttemptsResponse =
	| PartnerAttempt[]
	| {
			'@context'?: string;
			'@id'?: string;
			'@type'?: string;
			totalItems?: number;
			'hydra:totalItems'?: number;
			member?: PartnerAttempt[];
			'hydra:member'?: PartnerAttempt[];
			view?: {
				'@id'?: string;
				'@type'?: string;
				next?: string;
				previous?: string;
				first?: string;
				last?: string;
			};
	  };

const extractPartnerAttempts = (
	data: PartnerAttemptsResponse | null | undefined
): { attempts: PartnerAttempt[]; hasNextPage: boolean } => {
	if (!data) {
		return { attempts: [], hasNextPage: false };
	}

	if (Array.isArray(data)) {
		return { attempts: data, hasNextPage: false };
	}

	const attempts = data.member || data['hydra:member'] || [];
	return {
		attempts,
		hasNextPage: Boolean(data.view?.next),
	};
};

/** Prefer drives_fetched_at when backend updates an existing attempt instead of creating a new one */
const isAttemptAfterStart = (attempt: PartnerAttempt, startTime: Date): boolean => {
	// Allow slight clock skew / race between client startTime and server timestamps
	const effectiveStart = new Date(startTime.getTime() - 2 * 60 * 1000);

	const createdAt = attempt.createdAt ? new Date(attempt.createdAt) : null;
	const fetchedAt = attempt.parameter?.drives_fetched_at
		? new Date(attempt.parameter.drives_fetched_at)
		: null;

	if (fetchedAt && !Number.isNaN(fetchedAt.getTime()) && fetchedAt >= effectiveStart) {
		return true;
	}

	if (createdAt && !Number.isNaN(createdAt.getTime()) && createdAt >= effectiveStart) {
		return true;
	}

	return false;
};

/**
 * Get authentication data from localStorage
 */
const getAuthData = () => {
	try {
		const data = getByodAppSession();
		const organizationId = localStorage.getItem('BYOD-organizationId');

		if (data) {
			return {
				token: data.token_key || '',
				tenant: organizationId || '',
				workplace: data.workplace || null,
				userId: data.user?.id ?? null,
			};
		}
	} catch (error) {
		console.error('Error reading auth data from localStorage:', error);
	}
	return { token: '', tenant: '', workplace: null, userId: null };
};

/** Exclude system PersonalCacheLibrary from personal drive choices */
const isPersonalCacheLibrary = (drive: OneDriveDrive) =>
	(drive.name || '').toLowerCase() === 'personalcachelibrary';

interface OneDriveAuthState {
	accessToken: string | null;
	refreshToken: string | null;
	drives: OneDriveDrive[];
	selectedSource: OneDriveSourceType | null;
	selectedLocation: OneDriveLocationSelection | null;
	isConnected: boolean;
	isLoading: boolean;
	error: string | null;
}

interface UseOneDriveAuthReturn extends OneDriveAuthState {
	personalDrives: OneDriveDrive[];
	sharePointDrives: OneDriveDrive[];
	initiateOneDriveAuth: () => void;
	resetAuth: () => void;
	setSelectedSource: (source: OneDriveSourceType | null) => void;
	selectDriveRoot: (drive: OneDriveDrive) => void;
	selectDriveFolder: (drive: OneDriveDrive, folder: OneDriveRootItem) => void;
	buildPartnerParameters: (base?: Record<string, unknown>) => Record<string, unknown>;
}

const initialAuthState: OneDriveAuthState = {
	accessToken: null,
	refreshToken: null,
	drives: [],
	selectedSource: null,
	selectedLocation: null,
	isConnected: false,
	isLoading: false,
	error: null,
};

const buildLocationFromDrive = (
	drive: OneDriveDrive,
	folder: OneDriveRootItem | null
): OneDriveLocationSelection => {
	const source = (drive.source || 'personal_drive') as OneDriveSourceType;
	const location: OneDriveLocationSelection = {
		source,
		drive_id: drive.id,
		drive_name: source === 'personal_drive' ? 'OneDrive' : drive.name,
		folder_id: folder?.id ?? null,
		folder_name: folder?.name ?? null,
	};

	if (source === 'sharepoint_site') {
		location.site_id = drive.siteId ?? null;
		location.site_name = drive.siteName ?? null;
	}

	return location;
};

/**
 * Custom hook for Microsoft OneDrive OAuth 2.0
 * Mirrors the useGoogleAuth pattern — backend handles OAuth URL generation and callback.
 * Polls partner_attempts API (type: 'ms_one_drive') to detect a successful connection.
 */
export const useOneDriveAuth = (): UseOneDriveAuthReturn => {
	const [authState, setAuthState] = useState<OneDriveAuthState>(initialAuthState);
	const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clearPollingTimers = useCallback(() => {
		if (pollTimeoutRef.current) {
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = null;
		}
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, []);

	useEffect(() => () => clearPollingTimers(), [clearPollingTimers]);

	const personalDrives = useMemo(
		() =>
			authState.drives.filter(
				(d) => d.source === 'personal_drive' && !isPersonalCacheLibrary(d)
			),
		[authState.drives]
	);

	const sharePointDrives = useMemo(
		() => authState.drives.filter((d) => d.source === 'sharepoint_site'),
		[authState.drives]
	);

	/**
	 * Poll partner_attempts API to check for a successful OneDrive connection.
	 * Supports both Hydra collections and plain JSON arrays.
	 */
	const pollPartnerAttempts = useCallback(
		async (
			userId: number,
			workplaceId: number,
			startTime: Date
		): Promise<PartnerAttempt | null> => {
			let page = 1;

			try {
				let morePages = true;
				while (morePages) {
					const response = await apiService.get<PartnerAttemptsResponse>(`/partner_attempts`, {
						params: {
							page,
							itemsPerPage: 30,
							'user.id': userId,
							'workplace.id': workplaceId,
							type: 'ms_one_drive',
						},
					});

					// apiService returns AxiosResponse; some environments may already unwrap data
					const rawData = (response as any)?.data ?? response;
					const { attempts, hasNextPage } = extractPartnerAttempts(rawData);

					console.log(
						`[OneDrive OAuth] Page ${page}: fetched ${attempts.length} attempts`,
						{ isArray: Array.isArray(rawData) }
					);

					const recentAttempt = attempts.find((attempt) => {
						const hasToken = !!attempt.parameter?.access_token;
						const isAfterStart = isAttemptAfterStart(attempt, startTime);

						console.log('[OneDrive OAuth] Checking attempt:', {
							id: attempt.id,
							createdAt: attempt.createdAt,
							drivesFetchedAt: attempt.parameter?.drives_fetched_at,
							hasToken,
							isAfterStart,
							matches: isAfterStart && hasToken,
						});

						return isAfterStart && hasToken;
					});

					if (recentAttempt) {
						console.log(
							'[OneDrive OAuth] Found matching attempt on page',
							page,
							'— ID',
							recentAttempt.id
						);
						return recentAttempt;
					}

					if (hasNextPage) {
						page++;
					} else {
						console.log(
							'[OneDrive OAuth] No more pages — matching attempt not found yet'
						);
						morePages = false;
					}
				}

				return null;
			} catch (error) {
				console.error('[OneDrive OAuth] Error polling partner attempts:', error);
				return null;
			}
		},
		[]
	);

	/**
	 * Initiates Microsoft OneDrive OAuth flow.
	 * Calls backend to get the OAuth URL, opens it in a popup window,
	 * then polls partner_attempts until tokens are available or timeout occurs.
	 */
	const initiateOneDriveAuth = useCallback(async () => {
		try {
			setAuthState((prev) => ({
				...prev,
				isLoading: true,
				error: null,
			}));

			const { token, tenant, userId, workplace } = getAuthData();

			if (!token || !userId) {
				throw new Error('User not authenticated. Please log in again.');
			}

			const workplaceId = Number(resolveWorkplaceId(workplace));

			if (!workplaceId) {
				throw new Error('No workplace selected. Please select a workplace.');
			}

			if (!tenant) {
				throw new Error('No organization/tenant found. Please log in again.');
			}

			console.log(
				'[OneDrive OAuth] Starting OAuth flow for user:',
				userId,
				'workplace:',
				workplaceId,
				'tenant:',
				tenant
			);

			const startTime = new Date();

			const response = await apiService.post<OAuthInitiateResponse>(
				'/oauth/ms-onedrive/initiate',
				{
					workplaceId: Number(workplaceId),
					tenant: String(tenant),
				}
			);

			const { authUrl } = response.data;

			if (!authUrl) {
				throw new Error('Failed to get OAuth URL from backend');
			}

			console.log('[OneDrive OAuth] Opening OAuth window with URL:', authUrl);

			const authWindow = window.open(
				authUrl,
				'onedrive-oauth',
				'width=500,height=600,scrollbars=yes'
			);

			if (!authWindow) {
				throw new Error('Failed to open OAuth window. Please allow popups for this site.');
			}

			console.log('[OneDrive OAuth] OAuth window opened, starting to poll for partner attempts');

			let pollCount = 0;
			const maxPolls = 60;
			const pollInterval = 5000;

			const doPoll = async (): Promise<'stop' | 'continue'> => {
				pollCount++;
				console.log(
					`[OneDrive OAuth] Polling attempt ${pollCount}/${maxPolls} for user ${userId}, workplace ${workplaceId}`
				);

				const attempt = await pollPartnerAttempts(Number(userId), workplaceId, startTime);

				if (attempt && attempt.parameter?.access_token) {
					console.log('[OneDrive OAuth] Success! Partner attempt found (token present, not logged)');

					const closeAuthWindow = () => {
						try {
							if (authWindow && !authWindow.closed) {
								authWindow.close();
							}
						} catch (e) {
							console.log('[OneDrive OAuth] Could not close auth window:', e);
						}
					};
					console.log('[OneDrive OAuth] Closing auth window');
					closeAuthWindow();
					setTimeout(closeAuthWindow, 500);
					setTimeout(closeAuthWindow, 1500);

					const drives = attempt.parameter.drives || [];

					setAuthState({
						accessToken: attempt.parameter.access_token,
						refreshToken: attempt.parameter.encrypted_refresh_token || null,
						drives,
						selectedSource: null,
						selectedLocation: null,
						isConnected: true,
						isLoading: false,
						error: attempt.parameter.drives_fetch_error || null,
					});
					return 'stop';
				}

				if (pollCount >= maxPolls) {
					console.log('[OneDrive OAuth] Polling timeout reached after', pollCount, 'attempts');

					try {
						if (authWindow && !authWindow.closed) {
							authWindow.close();
						}
					} catch (e) {
						console.log('[OneDrive OAuth] Could not close auth window on timeout:', e);
					}

					setAuthState((prev) => ({
						...prev,
						isLoading: false,
						error: 'Authentication timed out. Please try again.',
					}));
					return 'stop';
				}

				return 'continue';
			};

			clearPollingTimers();
			pollTimeoutRef.current = setTimeout(async () => {
				console.log('[OneDrive OAuth] Starting polling...');
				const result = await doPoll();

				if (result === 'stop') return;

				pollIntervalRef.current = setInterval(async () => {
					const result = await doPoll();
					if (result === 'stop') {
						clearPollingTimers();
					}
				}, pollInterval);
			}, 500);
		} catch (error: any) {
			console.error('[OneDrive OAuth] Error initiating OAuth:', error);
			clearPollingTimers();
			setAuthState({
				...initialAuthState,
				error: error.message || 'Failed to initiate Microsoft OneDrive authentication',
			});
		}
	}, [pollPartnerAttempts, clearPollingTimers]);

	const setSelectedSource = useCallback((source: OneDriveSourceType | null) => {
		setAuthState((prev) => ({
			...prev,
			selectedSource: source,
			selectedLocation: null,
		}));
	}, []);

	const selectDriveRoot = useCallback((drive: OneDriveDrive) => {
		setAuthState((prev) => ({
			...prev,
			selectedLocation: buildLocationFromDrive(drive, null),
		}));
	}, []);

	const selectDriveFolder = useCallback((drive: OneDriveDrive, folder: OneDriveRootItem) => {
		setAuthState((prev) => ({
			...prev,
			selectedLocation: buildLocationFromDrive(drive, folder),
		}));
	}, []);

	/**
	 * Build partner_list.parameters from OAuth tokens + chosen drive/folder location.
	 */
	const buildPartnerParameters = useCallback(
		(base: Record<string, unknown> = {}): Record<string, unknown> => {
			const { refreshToken, selectedLocation } = authState;
			const params: Record<string, unknown> = { ...base };

			if (refreshToken) {
				params.encrypted_refresh_token = refreshToken;

				const tokenKey = Object.keys(params).find(
					(key) => key.toLowerCase() === 'token' || key.toLowerCase() === 'access_token'
				);
				if (tokenKey) {
					params[tokenKey] = refreshToken;
				}

				const refreshKey = Object.keys(params).find(
					(key) =>
						key.toLowerCase() === 'refresh_token' ||
						key.toLowerCase() === 'encrypted_refresh_token'
				);
				if (refreshKey && refreshKey !== 'encrypted_refresh_token') {
					params[refreshKey] = refreshToken;
				}
			}

			if (selectedLocation) {
				params.source = selectedLocation.source;
				params.drive_id = selectedLocation.drive_id;
				params.drive_name = selectedLocation.drive_name;
				params.folder_id = selectedLocation.folder_id;
				params.folder_name = selectedLocation.folder_name;

				if (selectedLocation.source === 'sharepoint_site') {
					params.site_id = selectedLocation.site_id ?? null;
					params.site_name = selectedLocation.site_name ?? null;
				} else {
					delete params.site_id;
					delete params.site_name;
				}
			}

			return params;
		},
		[authState]
	);

	const resetAuth = useCallback(() => {
		clearPollingTimers();
		setAuthState(initialAuthState);
	}, [clearPollingTimers]);

	return {
		...authState,
		personalDrives,
		sharePointDrives,
		initiateOneDriveAuth,
		resetAuth,
		setSelectedSource,
		selectDriveRoot,
		selectDriveFolder,
		buildPartnerParameters,
	};
};
